/**
 * Legal texts.
 *
 * Every company detail that is still missing carries the {{pendiente}} token,
 * which the page renders as a "pendiente de publicar" tag: no name, NIF,
 * address, deadline or carrier is ever invented. The shipping figures are NOT
 * written here either — they are read from GET /api/shipping so the legal text
 * always matches what the cart charges.
 */
import { formatPrice } from "@/lib/format";

export const LEGAL_SLUGS = [
  "aviso-legal",
  "privacidad",
  "cookies",
  "condiciones-de-compra",
  "envios-y-devoluciones",
];

const P = "{{pendiente}}";
const MAIL = "info@gruporitual.com";

export const legalDocs = (shipping = {}) => {
  const flat = shipping.configured && shipping.flat != null ? formatPrice(shipping.flat, "es") : null;
  const freeFrom =
    shipping.configured && shipping.free_from != null ? formatPrice(shipping.free_from, "es") : null;

  const costLine = flat
    ? `Coste de envío: ${flat} (IVA incluido) por pedido a España peninsular.`
    : `Coste de envío: ${P}`;
  const freeLine = freeFrom
    ? `Envío gratuito: en pedidos iguales o superiores a ${freeFrom} con entrega en España peninsular.`
    : `Envío gratuito a partir de: ${P}`;
  const termsShipping = flat
    ? `Gastos de envío: ${flat} (IVA incluido) para España peninsular${
        freeFrom ? `, gratis en pedidos desde ${freeFrom}` : ""
      }. El importe se muestra en el carrito antes de confirmar el pedido.`
    : `Los gastos de envío se indican antes de confirmar el pedido: ${P}`;

  return {
    "aviso-legal": {
      title: "Aviso legal",
      intro:
        "Información general sobre el titular de este sitio web, exigida por la Ley 34/2002 de Servicios de la Sociedad de la Información (LSSI-CE).",
      sections: [
        {
          h: "Titular del sitio web",
          p: [
            `Denominación social: ${P}`,
            `NIF / CIF: ${P}`,
            `Domicilio fiscal: ${P}`,
            `Correo electrónico de contacto: ${MAIL}`,
            `Teléfono: ${P}`,
            `Datos registrales: ${P}`,
            "Nombre comercial: Naguarà",
          ],
        },
        {
          h: "Objeto",
          p: [
            "Este sitio web informa sobre los cafés, la formación y los servicios para hostelería de Naguarà, y permite la compra online de café de especialidad con entrega en España peninsular.",
            "El uso del sitio implica la aceptación de este aviso legal y de las condiciones aplicables en cada momento.",
          ],
        },
        {
          h: "Propiedad intelectual e industrial",
          p: [
            `La marca, el logotipo, los textos, las fotografías y el diseño del sitio son titularidad de ${P} o se utilizan con la autorización correspondiente.`,
            "Queda prohibida su reproducción total o parcial sin autorización expresa.",
          ],
        },
        {
          h: "Responsabilidad",
          p: [
            `${P} no se responsabiliza del uso indebido de los contenidos ni de los daños derivados de interrupciones del servicio ajenas a su control.`,
          ],
        },
        {
          h: "Legislación aplicable",
          p: [
            `Estas condiciones se rigen por la legislación española. Para cualquier controversia serán competentes los juzgados y tribunales de ${P}, salvo que la normativa de consumo determine otro fuero.`,
          ],
        },
      ],
    },

    privacidad: {
      title: "Política de privacidad",
      intro:
        "Cómo tratamos los datos personales que nos facilitas, conforme al Reglamento (UE) 2016/679 (RGPD) y la LOPDGDD.",
      sections: [
        {
          h: "Responsable del tratamiento",
          p: [
            `Responsable: ${P} · NIF ${P}`,
            `Dirección: ${P}`,
            `Contacto para privacidad: ${MAIL}`,
            `Delegado de protección de datos: ${P}`,
          ],
        },
        {
          h: "Datos que tratamos y para qué",
          p: [
            "Formularios de contacto, hostelería y formación: nombre, negocio, correo, teléfono y el mensaje que escribas, con la única finalidad de responder a tu solicitud. Base jurídica: consentimiento.",
            "Newsletter: tu dirección de correo, para enviarte novedades. Base jurídica: consentimiento, revocable en cualquier momento.",
            "Pedidos: el código postal de entrega que introduces en el carrito (para calcular el envío) y los datos de facturación y envío que facilitas en la pasarela de pago. Base jurídica: ejecución del contrato.",
          ],
        },
        {
          h: "Conservación",
          p: [
            `Las solicitudes de información se conservan ${P}. Los datos fiscales de los pedidos se conservan durante los plazos legales obligatorios.`,
          ],
        },
        {
          h: "Destinatarios y encargados del tratamiento",
          p: [
            "Pasarela de pago: Stripe Payments Europe, Ltd.",
            "Envío de los correos de la web: plataforma Emergent (correo transaccional).",
            "Alojamiento y despliegue del sitio: plataforma Emergent.",
            `Transportista de los envíos: ${P}`,
            "No se ceden datos a terceros al margen de los anteriores, salvo obligación legal.",
          ],
        },
        {
          h: "Tus derechos",
          p: [
            `Puedes ejercer los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a ${MAIL}, acreditando tu identidad.`,
            "También puedes reclamar ante la Agencia Española de Protección de Datos (www.aepd.es).",
          ],
        },
      ],
    },

    cookies: {
      title: "Política de cookies",
      intro:
        "Qué cookies y qué almacenamiento local utiliza este sitio, y cómo puedes gestionarlos.",
      sections: [
        {
          h: "Almacenamiento técnico necesario",
          p: [
            "El sitio guarda en el almacenamiento local de tu navegador tu preferencia de idioma, el contenido del carrito y el código postal de entrega que introduces, para que la tienda funcione. No se usan para perfilarte ni se envían a terceros.",
          ],
        },
        {
          h: "Cookies de terceros",
          p: [
            "Pasarela de pago (Stripe): cookies necesarias para procesar el pago y prevenir el fraude. Solo se cargan cuando pulsas «Pagar» y pasas a su página segura.",
            "Tipografías de Google Fonts e imágenes servidas por Unsplash y Pexels: se cargan desde sus servidores, que registran la petición.",
          ],
        },
        {
          h: "Publicidad y analítica",
          p: [
            "No hay ninguna herramienta de analítica, medición o publicidad instalada en esta web, por lo que no se muestra banner de consentimiento. Si en el futuro se instala alguna, esta política y el consentimiento se actualizarán antes de activarla.",
          ],
        },
        {
          h: "Cómo desactivarlas",
          p: [
            "Puedes bloquear o eliminar las cookies y el almacenamiento local desde la configuración de tu navegador. Si los bloqueas, es posible que la tienda no funcione correctamente.",
          ],
        },
      ],
    },

    "condiciones-de-compra": {
      title: "Condiciones generales de compra",
      intro:
        "Condiciones aplicables a la venta de café a través de esta web, conforme al Real Decreto Legislativo 1/2007 de defensa de los consumidores.",
      sections: [
        {
          h: "Vendedor",
          p: [`${P} · NIF ${P} · ${P}`],
        },
        {
          h: "Productos y precios",
          p: [
            "Los cafés se venden en grano, en formato de 250 g. Los precios se muestran en euros e incluyen el IVA aplicable.",
            termsShipping,
            "Los cafés marcados como «Temporalmente no disponible» no pueden comprarse hasta que se confirme su ficha y su stock.",
          ],
        },
        {
          h: "Ámbito de venta",
          p: [
            "La compra online está disponible únicamente con entrega en España peninsular.",
            `Baleares, Canarias, Ceuta y Melilla todavía no pueden finalizar la compra en la web: escríbenos a ${MAIL} y te preparamos un presupuesto de envío. Tampoco se realizan envíos internacionales por ahora.`,
          ],
        },
        {
          h: "Proceso de compra y pago",
          p: [
            "Introduces el código postal de entrega en el carrito para ver el coste del envío, y el pago se realiza mediante tarjeta a través de Stripe. Naguarà no almacena los datos de tu tarjeta.",
            "Recibirás la confirmación del pedido por correo electrónico.",
          ],
        },
        {
          h: "Derecho de desistimiento",
          p: [
            "Dispones de 14 días naturales desde la recepción para desistir de la compra, salvo en los supuestos excluidos legalmente: al ser un producto alimenticio precintado, el desistimiento no es aplicable si el envase se ha abierto después de la entrega.",
            `Para ejercerlo, escribe a ${MAIL}. El procedimiento, la dirección de devolución y quién asume el transporte se detallan en «Envíos y devoluciones».`,
          ],
        },
        {
          h: "Garantías y reclamaciones",
          p: [
            `Si el producto llega en mal estado o no corresponde con tu pedido, escríbenos a ${MAIL} lo antes posible con una fotografía y lo resolvemos sin coste para ti.`,
            `Hoja de reclamaciones y resolución de litigios: ${P}`,
          ],
        },
      ],
    },

    "envios-y-devoluciones": {
      title: "Envíos, devoluciones y reembolsos",
      intro: "Zonas, costes y condiciones de los envíos y de las devoluciones de la tienda online.",
      sections: [
        {
          h: "Zonas de envío",
          p: [
            "Zona de envío actual: España peninsular.",
            `Baleares, Canarias, Ceuta y Melilla: todavía no disponibles en la tienda online. Si quieres un pedido a estas zonas, escríbenos a ${MAIL} y te preparamos un presupuesto de envío.`,
            "Envíos internacionales: no disponibles por ahora.",
          ],
        },
        {
          h: "Costes",
          p: [
            costLine,
            freeLine,
            "No aplicamos ningún otro recargo ni método de envío alternativo.",
          ],
        },
        {
          h: "Plazos",
          p: [
            `Plazo de preparación del pedido: ${P}`,
            `Plazo estimado de entrega: ${P}`,
            `Transportista: ${P}`,
          ],
        },
        {
          h: "Devoluciones",
          p: [
            "Derecho de desistimiento: 14 días naturales desde la recepción del pedido. Al tratarse de un producto alimenticio precintado, no es aplicable si el envase se ha abierto después de la entrega.",
            `Procedimiento: escribe a ${MAIL} indicando el número de pedido y te confirmamos cómo enviarlo.`,
            `Dirección de devoluciones: ${P}`,
            `Coste del transporte de la devolución: ${P}`,
          ],
        },
        {
          h: "Reembolsos",
          p: [
            "El reembolso se realiza por el mismo medio de pago que utilizaste en la compra, una vez recibida y comprobada la devolución, en un plazo máximo de 14 días naturales desde que ejercitas el desistimiento.",
            "Si el pedido llegó dañado o incorrecto, el reembolso o la reposición no tienen ningún coste para ti.",
          ],
        },
      ],
    },
  };
};
